import { Component, OnInit, OnDestroy } from "@angular/core";
import { Store, Action } from "@ngrx/store";
import { Actions } from "@ngrx/effects";
import { AppState } from "@store/app-state";
import { Observable } from "rxjs/Observable";
import { ConstantsStore } from "@store/constants";
import { WorkshopsStore } from "@store/workshops";
import { TutorAllocationsStore } from "@store/tutor-allocations";
import { Subscription } from "rxjs/Subscription";
import { TutorTimeslots } from "@models/tutor-timeslots";
import { Term } from "@models/term";
import { Workshop } from "@models/workshop";
import { Day } from "@models/day";
import { Campus } from "@models/campus";
import { TwoWeekBlock } from "@models/two-week-block";
import { Window } from "@models/window";
import { TimeslotsParams } from "@services/booking";
import {
  CompleterData,
  CompleterService,
  CompleterItem } from "ng2-completer";
import {
  FETCH_TIMESLOTS,
  ADD_BOOKING,
  REPLACE_BOOKING,
  REMOVE_BOOKING
} from "@reducers/bookings";
import { FETCH_WORKSHOPS } from "@reducers/workshops";
import { BookingsStore } from "@store/bookings";

@Component({
  selector: 'workshop-bookings',
  templateUrl: './workshop-bookings.pug',
})
export class WorkshopBookingsComponent implements OnInit, OnDestroy {

  public terms: Term[] = []
  public workshops: Workshop[] = []
  public campuses: Campus[] = []
  public days: Day[] = []

  public activeTerm: Term
  public activeWorkshop: Workshop
  public activeCampus: Campus
  public activeDay: Day
  public activeTwoWeekBlock: TwoWeekBlock
  public activeWindow: Window

  public workshopsSubscription: Subscription
  public constantsSubscription: Subscription
  public bookingsSubscription: Subscription
  public actionsSubscription: Subscription

  public termCompleter: CompleterData
  public termSearchStr: string
  public workshopCompleter: CompleterData
  public workshopSearchStr: string
  public campusCompleter: CompleterData
  public campusSearchStr: string
  public twoWeekBlockCompleter: CompleterData
  public twoWeekBlockSearchStr: string

  public timeslots: TutorTimeslots[]

  constructor(
    private store: Store<AppState>,
    private completerService: CompleterService,
    private actions: Actions,
  ) {}

  ngOnInit() {
    const workshopsStore  = this.store.select("workshopsReducer")
    const constantsStore  = this.store.select("constantsReducer")
    const bookingsStore   = this.store.select("bookingsReducer")

    this.constantsSubscription = constantsStore.subscribe((s: ConstantsStore) => {
      this.days           = s.constants.days
      this.terms          = s.constants.terms.filter(t => !t.isHoliday)
      this.campuses       = s.constants.campuses
    })

    this.workshopsSubscription = workshopsStore.subscribe((s: WorkshopsStore) => {
      this.workshops      = s.workshops
      this.setupStateBasedOnFetchedWorkshops()
    })

    this.bookingsSubscription = bookingsStore.subscribe((s: BookingsStore) => {
      this.timeslots      = s.timeslots
    })

    this.actionsSubscription =
      this.actions
        .ofType(REPLACE_BOOKING, ADD_BOOKING)
        .subscribe(() => this.fetchTimeslots())

    this.setupStateBasedOnFetchedWorkshops()
  }

  setupStateBasedOnFetchedWorkshops() {
    if (this.workshops.length > 0) {
      this.activeTerm         = this.terms[0]
      this.activeTwoWeekBlock = this.activeTerm.twoWeekBlocks[0]
      this.activeWorkshop     = this.workshops[0]
      this.activeCampus       = this.campuses[0]
      this.activeDay          = this.getFirstNonDisabledDay()

      this.setupCompleters()
      this.populateActiveWindow()
    }
  }

  setupCompleters() {
    this.populateTermCompleterData()
    this.populateTwoWeekBlockCompleterData()
    this.populateWorkshopCompleterData()
    this.populateCampusCompleterData()
  }

  fetchTimeslots() {
    if (this.activeWindow && this.activeTwoWeekBlock) {
      const payload = {
        twoWeekBlockIndex: this.activeTwoWeekBlock.index,
        windowId: this.activeWindow.id
      }
      this.store.dispatch({ type: FETCH_TIMESLOTS, payload })
    }
    else {
      this.timeslots = []
    }
  }

  isDayActive(dayLetter: string): boolean {
    return this.activeDay.code === dayLetter
  }

  isDayDisabled(dayCode: string): boolean {
    return !this.getActiveWindow(dayCode)
  }

  selectedTerm(e: CompleterItem) {
    if (e && e.originalObject) {
      this.activeTerm = (e.originalObject as Term)
      this.store.dispatch({ type: FETCH_WORKSHOPS, payload: this.activeTerm.id })
    }
  }

  selectedCampus(e: CompleterItem) {
    if (e && e.originalObject) {
      this.activeCampus = (e.originalObject as Campus)
      this.activeDay = this.getFirstNonDisabledDay()
      this.populateActiveWindow()
    }
  }

  selectedWorkshop(e: CompleterItem) {
    if (e && e.originalObject) {
      this.activeWorkshop = (e.originalObject as Workshop)
      this.activeDay = this.getFirstNonDisabledDay()
      this.populateActiveWindow()
    }
  }

  selectedTwoWeekBlock(e: CompleterItem) {
    if (e && e.originalObject) {
      this.activeTwoWeekBlock = (e.originalObject as TwoWeekBlock)
      this.populateActiveWindow()
    }
  }

  clickedDay(dayLetter: string) {
    if (this.isDayDisabled(dayLetter)) { return }
    this.activeDay = this.days.find((d: Day) => d.code === dayLetter)
    this.populateActiveWindow()
  }

  populateTermCompleterData() {
    this.termCompleter  = 
      this.completerService.local(this.terms, "name", "name")
  }

  populateTwoWeekBlockCompleterData() {
    this.twoWeekBlockCompleter = 
      this.completerService.local(this.activeTerm.twoWeekBlocks, "name", "name")
  }

  populateWorkshopCompleterData() {
    this.workshopCompleter = 
      this.completerService.local(this.workshops, "displayName", "displayName")
  }

  populateCampusCompleterData() {
    this.campusCompleter = 
      this.completerService.local(this.campuses, "name", "name")
  }

  populateActiveWindow() {
    this.activeWindow = this.getActiveWindow()
    this.fetchTimeslots()
  }

  getActiveWindow(dayCode: string = null): Window {
    return this.activeWorkshop.windows.find((w: Window) => {

      const targetDay = this.days.find(d => d.code == dayCode)
      let targetDayId = null
      if (!targetDay) {
        targetDayId = this.activeDay.id
      }
      else {
        targetDayId = targetDay.id
      }

      return  w.campusId == this.activeCampus.id &&
              w.dayId == targetDayId
    })
  }

  getFirstNonDisabledDay(): Day {
    return this.days.find((d) => !this.isDayDisabled(d.code))
  }

  ngOnDestroy() {
    this.workshopsSubscription.unsubscribe()
    this.constantsSubscription.unsubscribe()
    this.actionsSubscription.unsubscribe()
  }

}



// WEBPACK FOOTER //
// /Users/jason/src/matrix/client/src/app/components/workshop-bookings.ts